import { type FunctionComponent, useEffect } from 'react';

import { Checkbox } from '@dr.pogodin/react-utils';

import { VideoModalities } from 'components/admin/VideoModalities';

import type { ArtistStatusT, ModalityT } from 'utils';

import IconClose from 'assets/images/icon-cross-24px.svg';

import './style.scss';

export type FiltersT = {
  customModalities?: string[];
  modalities?: ModalityT[];
  status?: ArtistStatusT[];
};

export function countFilters(filters: FiltersT): number {
  let res = 0;
  if (filters.customModalities?.length || filters.modalities?.length) ++res;
  if (filters.status?.length) ++res;
  return res;
}

type PropsT = {
  onChange: (value: FiltersT) => void;
  onClose: () => void;
  value: FiltersT;
};

const STATUSES: ArtistStatusT[] = ['Active', 'Pending Verification', 'Inactive'];

export const FiltersPanel: FunctionComponent<PropsT> = ({
  onChange,
  onClose,
  value,
}) => {
  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  const handleStatusToggle = (status: ArtistStatusT) => {
    const currentStatus = value.status ?? [];
    const newStatus = currentStatus.includes(status)
      ? currentStatus.filter((s) => s !== status)
      : [...currentStatus, status];

    onChange({
      ...value,
      status: newStatus.length > 0 ? newStatus : undefined,
    });
  };

  return (
    <>
      <div
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onClose();
        }}
        role="presentation"
        styleName="backdrop"
      />
      <div styleName="modalContainer">
        <div styleName="header">
          <h2 styleName="title">Filters</h2>
          <div styleName="headerActions">
            <button
              disabled={!countFilters(value)}
              onClick={() => {
                onChange({});
              }}
              styleName="clearAllLink"
              type="button"
            >
              Clear all
            </button>
            <button
              onClick={onClose}
              styleName="closeButton"
              type="button"
            >
              <IconClose />
            </button>
          </div>
        </div>

        <div styleName="filters">
          <div styleName="filterGroup">
            <div styleName="checkboxGroup">
              <div styleName="checkboxGroupTitle">By Status</div>
              {STATUSES.map((status) => (
                <Checkbox
                  checked={value.status?.includes(status) ?? false}
                  key={status}
                  label={status}
                  onChange={() => {
                    handleStatusToggle(status);
                  }}
                />
              ))}
            </div>
          </div>

          <div styleName="filterGroup">
            <VideoModalities
              customModalities={value.customModalities ?? []}
              label="By Modality"
              modalities={value.modalities ?? []}
              onChange={(modalities, customModalities) => {
                onChange({
                  ...value,
                  customModalities,
                  modalities,
                });
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
};
